import {
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Switch,
  useDisclosure,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { useDeleteUserMutation } from "@src/__generated__/graphql";
import { ActivateUserModal } from "@src/components/modules/users/listing/table/ActivateUserModal";
import { DeactivateUserModal } from "@src/components/modules/users/listing/table/DeactivateUserModal";
import { ModalConfirm, RemoveButton } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { onError } from "@src/utils/apollo";
import { cannot } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { type FC, type PropsWithChildren, useContext } from "react";
import type { UserModel } from "../models";
import { PageUsersCtx } from "../PageUsers";
import ModalReassignActiveAssets from "./ModalReassignActiveAssets";

export const PopoverRowActions: FC<
  PropsWithChildren<{
    user: UserModel;
  }>
> = observer(function PopoverRowActions({ user }) {
  const appStore = useStore();
  const { editUserModalStore } = appStore;
  const store = useContext(PageUsersCtx);
  const deleteUserDialog = useDisclosure();
  const archiveUserDialog = useDisclosure();
  const activateUserDialog = useDisclosure();

  const [deleteUser] = useDeleteUserMutation({
    variables: { id: user.id },
    ...onError(),
    onCompleted() {
      store.refetch?.();
      appStore.UIStore.toast({
        title: t`User was deleted`,
        status: "success",
      });
    },
  });

  const toggleActive = () => {
    if (user.archived_at) {
      activateUserDialog.onOpen();
    } else {
      archiveUserDialog.onOpen();
    }
  };

  return (
    <HStack>
      <FormControl
        alignItems="center"
        display="flex"
        w="auto"
        isDisabled={
          store.userType === "internal"
            ? cannot("user_updateAllfredData")
            : cannot("user_updateAllfredData", "user_updateScopeUser")
        }
      >
        <FormLabel mb="0" htmlFor="toggle-user-activity">
          <Trans>Active</Trans>
        </FormLabel>
        <Switch
          id="toggle-user-activity"
          isChecked={!user.archived_at}
          onChange={toggleActive}
        />
      </FormControl>
      <IconButton
        aria-label={t`Edit user`}
        colorScheme="grey"
        icon={<Icon name="edit-02" w="5" h="5" />}
        isDisabled={
          store.userType === "internal"
            ? cannot(
                "user_updateAllfredData",
                "user_update_all",
                "user_updateWorkData",
              )
            : cannot(
                "user_updateAllfredData",
                "user_update_all",
                "user_updateWorkData",
                "user_updateScopeUser",
              )
        }
        onClick={() =>
          editUserModalStore.modalState.onOpen({
            userId: user.id,
            userType: user.type,
            onSubmit: () => store.refetch?.(),
          })
        }
        variant="ghost"
      />
      <IconButton
        aria-label={t`Change user password`}
        colorScheme="grey"
        icon={<Icon name="key-01" w="5" h="5" />}
        isDisabled={cannot("user_update_all")}
        onClick={() =>
          store.changeUserPasswordStore.disclosure.onOpen({
            user,
          })
        }
        variant="ghost"
      />
      <RemoveButton
        aria-label={t`Delete user`}
        isDisabled={
          store.userType === "internal"
            ? cannot(
                "user_updateAllfredData",
                "user_update_all",
                "user_updateWorkData",
              )
            : cannot(
                "user_updateAllfredData",
                "user_update_all",
                "user_updateWorkData",
                "user_updateScopeUser",
              )
        }
        onClick={() => deleteUserDialog.onOpen()}
      />
      <DeactivateUserModal
        {...archiveUserDialog.getDisclosureProps()}
        {...archiveUserDialog}
        userId={user.id}
        canHaveAssets={user.canHaveAssets}
        onConfirm={() => {
          store.refetch?.();
          appStore.UIStore.toast({
            title: t`User was deactivated`,
            status: "success",
          });
        }}
      />
      {user.hasActiveAssets ? (
        <ModalReassignActiveAssets
          {...deleteUserDialog.getDisclosureProps()}
          {...deleteUserDialog}
          contextType="remove"
          userId={user.id}
          onConfirm={deleteUser}
        />
      ) : (
        <ModalConfirm
          {...deleteUserDialog.getDisclosureProps()}
          {...deleteUserDialog}
          onConfirm={deleteUser}
        />
      )}
      <ActivateUserModal
        {...activateUserDialog.getDisclosureProps()}
        {...activateUserDialog}
        userId={user.id}
        onConfirm={() => {
          store.refetch?.();
          appStore.UIStore.toast({
            title: t`User was activated`,
            status: "success",
          });
        }}
      />
    </HStack>
  );
});
